/**
 * Generische Loading/Toast-Effects.
 *
 * Pattern: jede AsyncActionGroup wird hier registriert. Die Effect-Creators
 * aus pd-spa-helper bauen daraus:
 *   - createLoadingEffect(groups, { modal: true | smallBackground: true })
 *   - createRemoveLoadingEffect(groups)            entfernt Loading bei .success / .fail
 *   - createSuccessToastEffect(groups)             Toast bei .success (wenn config.successTxt gesetzt)
 *   - createErrorToastEffect(groups, options)      Toast bei .fail
 *
 * `modalGroups` blockieren die UI mit einem Modal-Loader, `backgroundGroups`
 * zeigen einen unaufdringlichen Indikator.
 *
 * Fehlertexte: Ein `.fail` trägt den `BusinessError` aus `validateFunctionResult`
 * mit dem Statuscode des Backends (`handleError` aus pd-provider-firebase-functions:
 * 400 Validation, 401, 403, 404, 409 Conflict, 500; App-Codes negativ, z. B.
 * -160 E-Mail vergeben). `getErrorMessage` ist die EINZIGE Stelle, an der Codes
 * zu Nutzertexten werden (Muster djkollektiv).
 */

import { msg } from "@lit/localize";
import {
  createLoadingEffect,
  createRemoveLoadingEffect,
  createSuccessToastEffect,
  createErrorToastEffect,
  type AsyncActionGroupBase,
} from "@progressive-development/pd-spa-helper";

import { loadProfile, updateProfile } from "../../modules/members/profile/store/profile-actions.js";
import {
  loadBookmarks,
  addBookmark,
  updateBookmark,
  deleteBookmark,
} from "../../modules/members/bookmarks/store/bookmarks-actions.js";
import { loadAufmacher, loadVerzeichnisse } from "../../modules/home/store/startseite-actions.js";
import {
  loadBvBeschluesse,
  loadBvSitze,
  loadBvSitzungen,
} from "../../modules/bezirksvertretung/store/bv-actions.js";
import {
  loadGremienKennzahlen,
  loadGremiumBesetzung,
  loadGremiumDrucksachen,
  loadGremiumSitzungen,
} from "../../modules/gremien/store/gremien-actions.js";
import { loadWahlErgebnis } from "../../modules/wahlen/store/wahlen-actions.js";
import { loadSitzungenListe } from "../../modules/sitzungen/store/sitzungen-actions.js";
import { loadParteiSicht } from "../../modules/parteien/store/parteien-actions.js";
import {
  loadBodenrichtwerte,
  loadGebietDrucksachen,
  loadGebietZahlen,
  loadVergleich,
  loadWahllokale,
} from "../../modules/gebiet/store/gebiet-actions.js";
import {
  loadDrucksache,
  loadDrucksachenListe,
  loadEntscheidungenStammdaten,
  loadSitzung,
} from "../../modules/entscheidungen/store/entscheidungen-actions.js";

const modalGroups: AsyncActionGroupBase[] = [
  updateProfile,
  addBookmark,
  updateBookmark,
  deleteBookmark,
];

const backgroundGroups: AsyncActionGroupBase[] = [
  loadProfile,
  loadBookmarks,
  loadVerzeichnisse,
  loadAufmacher,
  loadDrucksachenListe,
  loadDrucksache,
  loadSitzung,
  loadEntscheidungenStammdaten,
  loadGebietDrucksachen,
  loadGebietZahlen,
  loadWahllokale,
  loadVergleich,
  loadBodenrichtwerte,
  loadBvSitzungen,
  loadBvBeschluesse,
  loadBvSitze,
  loadGremienKennzahlen,
  loadGremiumSitzungen,
  loadGremiumDrucksachen,
  loadGremiumBesetzung,
  loadWahlErgebnis,
  loadSitzungenListe,
  loadParteiSicht,
];

const allGroups = [...modalGroups, ...backgroundGroups];

// =============================================================================
// Fehlertexte je Statuscode
// =============================================================================

const unknownError = (): string =>
  msg("Ein unbekannter Fehler ist aufgetreten.", { id: "app.error.unknown" });

const getErrorMessage = (errorCode: number): string => {
  const errorMessages: Record<number, string> = {
    // HTTP-Statuscodes (pd-provider-firebase-functions: AppError.statusCode)
    400: msg("Die Anfrage war ungültig.", { id: "app.error.badRequest" }),
    401: msg("Sie sind nicht angemeldet.", { id: "app.error.unauthorized" }),
    403: msg("Sie haben keine Berechtigung für diese Aktion.", { id: "app.error.forbidden" }),
    404: msg("Die angeforderten Daten wurden nicht gefunden.", { id: "app.error.notFound" }),
    409: msg("Das steht im Konflikt mit vorhandenen Daten.", { id: "app.error.conflict" }),
    500: msg("Ein Serverfehler ist aufgetreten. Bitte versuchen Sie es später erneut.", {
      id: "app.error.serverError",
    }),
    // App-Codes (negativ, aus pd-provider-firebase-functions bzw. pd-spa-helper)
    [-160]: msg("Diese E-Mail-Adresse wird bereits verwendet.", {
      id: "app.error.emailAlreadyUsed",
    }),
  };
  return errorMessages[errorCode] || unknownError();
};

// =============================================================================
// Effects
// =============================================================================

export const modalLoadingEffect = createLoadingEffect(modalGroups, { modal: true });
export const bgLoadingEffect = createLoadingEffect(backgroundGroups, {
  smallBackground: true,
});
export const removeLoadingEffect = createRemoveLoadingEffect(allGroups);
export const successToastEffect = createSuccessToastEffect(allGroups);
export const errorToastEffect = createErrorToastEffect(allGroups, {
  getErrorMessage,
  defaultErrorMessage: unknownError(),
});

export const appLoadingEffects = [
  modalLoadingEffect,
  bgLoadingEffect,
  removeLoadingEffect,
  successToastEffect,
  errorToastEffect,
];
